import { USER_ROLES, type ContactSort, type ScreenContextDto } from '@rimati/shared';
import { ApiError } from './client.js';
import { storeMediaApi } from './endpoints.js';
import { rimatiChatChannel, type RimatiChatPurpose } from './endpoints.js';
import { useEffect, useRef } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  IncidenteDto,
  SugestoesDto,
  TicketDto,
  TicketDaFilaDto,
  VisaoDoOperadorDto,
  AtivacaoFunilDto,
  OnboardingDto,
  AdminChatSessionDto,
  PriorityFeedDto,
  AdminChatViewDto,
  AdminPeriod,
  DecideProposalRequest,
  SetupSessionDto,
  AutomationResultDto,
  AgentTodayDto,
  CalendarSummaryDto,
  NoShowsByClientDto,
  ActivityDto,
  AgentDto,
  AnalyticsPeriod,
  AppointmentDto,
  CreateWaitlistEntryRequest,
  WaitlistEntryDto,
  AvailabilityRuleDto,
  ClinicServiceDto,
  ClinicalResourceDto,
  PatientProfileDto,
  ClinicalAccessLogDto,
  UpdatePatientProfileRequest,
  AutomationDto,
  AutomationTemplate,
  ChangePasswordRequest,
  ChannelDto,
  CompanyDto,
  CreateUserRequest,
  CreateUserResultDto,
  DashboardDto,
  CapabilityHealthReportDto,
  IntegrationHomologationDto,
  IntegrationHomologationKind,
  IntegrationHomologationReportDto,
  ExternalReadinessReportDto,
  UpdateIntegrationHomologationRequest,
  DiagnosticsDto,
  InsightsDto,
  MediaAnalyticsDto,
  MediaAnalyticsKind,
  MediaAnalyticsState,
  FollowUpDto,
  KnowledgeItemDto,
  ContactDto,
  ConversationDto,
  MessageDto,
  OpportunityDto,
  Paginated,
  PipelineDto,
  ProfessionalDto,
  PlaygroundResultDto,
  PlaygroundTurnDto,
  PlaygroundMemory,
  ToolName,
  UpdateAgentRequest,
  UpdateCompanyRequest,
  UpdateContactRequest,
  UpdateUserRequest,
  UserDto,
} from '@rimati/shared';
import { chatViewFromReply } from '../features/rimati-chat/chatView';
import {
  agentSetupApi,
  priorityApi,
  rimatiChatApi,
  configSetupApi,
  activitiesApi,
  agentsApi,
  analyticsApi,
  mediaGovernanceApi,
  dataGovernanceApi,
  availabilityRulesApi,
  clinicServicesApi,
  professionalsApi,
  clinicalResourcesApi,
  channelsApi,
  companyApi,
  diagnosticsApi,
  appointmentsApi,
  automationsApi,
  followUpsApi,
  knowledgeApi,
  contactsApi,
  patientsApi,
  conversationsApi,
  opportunitiesApi,
  pipelinesApi,
  usersApi,
  onboardingApi,
  plataformaApi,
  suporteApi,
} from './endpoints.js';
import { queryKeys } from './queryKeys.js';
import { useAuthStore } from '../stores/auth.store.js';

/** Hooks de dados remotos. A invalidacao por evento vive no RealtimeProvider. */

export function usePipelines(): UseQueryResult<PipelineDto[]> {
  return useQuery({
    queryKey: queryKeys.pipelines,
    queryFn: pipelinesApi.list,
    staleTime: 300_000,
  });
}

export function useOpportunities(
  filters: Parameters<typeof opportunitiesApi.list>[0] = {},
): UseInfiniteQueryResult<InfiniteData<Paginated<OpportunityDto>>> {
  return useInfiniteQuery({
    queryKey: [...queryKeys.opportunities, filters],
    queryFn: ({ pageParam }) => opportunitiesApi.list({ limit: 100, ...filters, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last, pages) => (last.hasMore ? pages.length + 1 : undefined),
  });
}

/** Resolve only the linked opportunity, including closed ones, through the same authorized list. */
export function useLinkedOpportunity(id: string | null): UseQueryResult<OpportunityDto | null> {
  return useQuery({
    queryKey: [...queryKeys.opportunities, 'linked', id],
    queryFn: async () => {
      const result = await opportunitiesApi.list({ opportunityId: id as string, limit: 1 });
      return result.items.find((item) => item.id === id) ?? null;
    },
    enabled: Boolean(id),
  });
}

/**
 * R074 - renomear e reordenar etapas.
 *
 * Invalida o funil E as oportunidades: o quadro é desenhado a partir das etapas,
 * e uma tela que continuasse com a ordem antiga faria alguém arrastar um cartão
 * para a coluna errada.
 */
export function useSetStages() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entrada: { pipelineId: string; stages: { id: string; name: string }[] }) =>
      pipelinesApi.setStages(entrada.pipelineId, entrada.stages),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.pipelines });
      void client.invalidateQueries({ queryKey: queryKeys.opportunities });
    },
  });
}

export function useContactOpportunities(
  contactId: string | undefined,
): UseQueryResult<Paginated<OpportunityDto>> {
  return useQuery({
    queryKey: queryKeys.opportunitiesByContact(contactId ?? ''),
    queryFn: () => opportunitiesApi.list({ contactId: contactId as string }),
    enabled: Boolean(contactId),
  });
}

/** Quantos contatos por página. O servidor aceita até 100. */
const CONTATOS_POR_PAGINA = 50;

/**
 * R102 - a lista de contatos passou a ter CONTINUAÇÃO.
 *
 * Ela pedia 50 e parava ali, sem dizer. A semente da demonstração tem 72
 * contatos, e uma corrida da suíte acrescenta alguns: mais de vinte pessoas já
 * eram invisíveis na tela de hoje, e a única forma de descobrir isso era contar
 * no banco. Não é um risco futuro com "volume real" — já acontecia, na base
 * pequena que todo mundo usa para demonstrar o produto.
 *
 * Uma lista truncada em silêncio é pior que uma lista paginada, porque ela
 * responde à pergunta errada com a confiança da pergunta certa: quem procura um
 * cliente, não acha, e conclui que ele não está cadastrado.
 *
 * O servidor já sabia disso e já dizia — `total` e `hasMore` vêm em toda
 * resposta desde sempre. O que faltava era a tela escutar.
 */
export function useContacts(
  search: string,
  sort: ContactSort = 'recent',
): UseInfiniteQueryResult<InfiniteData<Paginated<ContactDto>>> {
  return useInfiniteQuery({
    queryKey: [...queryKeys.contacts, { search, sort }],
    queryFn: ({ pageParam }) =>
      contactsApi.list({
        search: search || undefined,
        sort,
        limit: CONTATOS_POR_PAGINA,
        page: pageParam,
      }),
    initialPageParam: 1,
    /*
      `undefined` encerra: o React Query trata isso como "não há próxima", e é
      o que faz `hasNextPage` virar falso e o botão sumir.

      A decisão vem de `hasMore`, e não de comparar tamanhos — `items.length <
      limit` erraria justamente no caso limítrofe em que o total é múltiplo do
      limite, oferecendo uma página vazia a quem clicasse.
    */
    getNextPageParam: (ultima, todas) => (ultima.hasMore ? todas.length + 1 : undefined),
  });
}

export function useContact(id: string | undefined): UseQueryResult<ContactDto> {
  return useQuery({
    queryKey: queryKeys.contact(id ?? ''),
    queryFn: () => contactsApi.get(id as string),
    enabled: Boolean(id),
  });
}

export function usePatientProfile(
  contactId: string | undefined,
  enabled = true,
): UseQueryResult<PatientProfileDto | null> {
  return useQuery({
    queryKey: queryKeys.patient(contactId ?? ''),
    queryFn: () => patientsApi.get(contactId as string),
    enabled: Boolean(contactId) && enabled,
  });
}

export function useClinicalAccessLog(enabled = true): UseQueryResult<ClinicalAccessLogDto[]> {
  return useQuery({
    queryKey: queryKeys.clinicalAccessLog,
    queryFn: () => patientsApi.accessLog(),
    enabled,
    staleTime: 15_000,
  });
}

export function useUpdatePatientProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, body }: { contactId: string; body: UpdatePatientProfileRequest }) =>
      patientsApi.update(contactId, body),
    onSuccess: (profile) => {
      client.setQueryData(queryKeys.patient(profile.contactId), profile);
      void client.invalidateQueries({ queryKey: queryKeys.clinicalAccessLog });
    },
  });
}

export function useRevokePatientConsent() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) => patientsApi.revokeConsent(contactId),
    onSuccess: (profile) => {
      client.setQueryData(queryKeys.patient(profile.contactId), profile);
      void client.invalidateQueries({ queryKey: queryKeys.clinicalAccessLog });
    },
  });
}

export function useConversations(
  search: string,
  needsAttention = false,
): UseQueryResult<Paginated<ConversationDto>> {
  return useQuery({
    queryKey: [...queryKeys.conversationList, { search, needsAttention }],
    queryFn: () =>
      conversationsApi.list({
        search: search || undefined,
        limit: 60,
        ...(needsAttention ? { needsAttention: true } : {}),
      }),
  });
}

/**
 * RIM-164 - quantas conversas esperam uma pessoa agora.
 *
 * Consulta propria, com limite 1: o numero vive no menu lateral, em toda tela,
 * e nao pode depender de a lista de Conversas estar carregada.
 */
export function useAttentionCount(enabled = true): UseQueryResult<number> {
  return useQuery({
    queryKey: [...queryKeys.conversationList, 'atencao'],
    queryFn: async () => (await conversationsApi.list({ needsAttention: true, limit: 1 })).total,
    enabled,
  });
}

export function useMessages(
  conversationId: string | undefined,
): UseQueryResult<Paginated<MessageDto>> {
  return useQuery({
    queryKey: queryKeys.messages(conversationId ?? ''),
    queryFn: () => conversationsApi.messages(conversationId as string, { limit: 80 }),
    enabled: Boolean(conversationId),
  });
}

export function useHandoffContext(conversationId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.handoffContext(conversationId ?? ''),
    queryFn: () => conversationsApi.handoffContext(conversationId as string),
    enabled: Boolean(conversationId) && enabled,
    staleTime: 15_000,
  });
}

export function useActivities(
  limit = 20,
  /** RIM-181 - o painel do Inicio pede so as acoes do produto. */
  actorTypes?: string,
): UseQueryResult<Paginated<ActivityDto>> {
  return useQuery({
    queryKey: [...queryKeys.activities, { limit, actorTypes }],
    queryFn: () => activitiesApi.list({ limit, ...(actorTypes ? { actorTypes } : {}) }),
  });
}

/**
 * ASSISTENTES WOW - a conversa que ensina a assistente.
 *
 * Ela invalida `agents` e `knowledge` ao aprovar porque e exatamente isso que
 * uma aprovacao muda: a orientacao entra nas instrucoes do assistente, a
 * informacao entra no conhecimento. Sem isso, a aba ao lado continuaria
 * mostrando a configuracao antiga ate alguem recarregar.
 */
/**
 * CONFIGURAR COM RIMATI - os mesmos hooks servem as duas conversas.
 *
 * `agent` e a conversa com a Livia; `company` e a configuracao da empresa. A
 * chave de cache e a rota mudam com o escopo, e nada mais.
 */
type EscopoDeSetup = 'agent' | 'company';

const apiDoEscopo = (escopo: EscopoDeSetup) =>
  escopo === 'company' ? configSetupApi : agentSetupApi;
const chaveDoEscopo = (escopo: EscopoDeSetup) =>
  escopo === 'company' ? queryKeys.configSetup : queryKeys.agentSetup;

export function useAgentSetup(escopo: EscopoDeSetup = 'agent'): UseQueryResult<SetupSessionDto> {
  return useQuery({
    queryKey: chaveDoEscopo(escopo),
    queryFn: () => apiDoEscopo(escopo).session(),
  });
}

export function useSendSetupMessage(escopo: EscopoDeSetup = 'agent') {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => apiDoEscopo(escopo).send(text),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: chaveDoEscopo(escopo) });
    },
  });
}

export function useDecideProposal(escopo: EscopoDeSetup = 'agent') {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DecideProposalRequest }) =>
      apiDoEscopo(escopo).decide(id, body),
    onSuccess: () => {
      void useAuthStore.getState().restore();
      void client.invalidateQueries({ queryKey: queryKeys.onboarding });
      /*
        A aprovacao mexe em varias telas ao mesmo tempo (secao 50): horario vai
        para a Agenda, orientacao para Assistentes, automacao para Automacoes,
        informacao para Conhecimento. Invalidar so a conversa deixaria a pessoa
        aprovando uma mudanca que a tela ao lado continua negando.
      */
      for (const chave of [
        chaveDoEscopo(escopo),
        queryKeys.agents,
        queryKeys.knowledge,
        queryKeys.automations,
        queryKeys.availabilityRules,
        queryKeys.channels,
        ...queryKeys.analytics,
      ]) {
        void client.invalidateQueries({ queryKey: chave });
      }
    },
  });
}

export function useContactActivities(
  contactId: string | undefined,
): UseQueryResult<Paginated<ActivityDto>> {
  return useQuery({
    queryKey: queryKeys.activitiesByContact(contactId ?? ''),
    queryFn: () => activitiesApi.list({ contactId: contactId as string, limit: 40 }),
    enabled: Boolean(contactId),
  });
}

/** RIM-188 - contagens do dia para o topo de Assistentes. */
export function useAgentToday(): UseQueryResult<AgentTodayDto> {
  return useQuery({
    queryKey: [...queryKeys.agents, 'today'],
    queryFn: () => agentsApi.today(),
  });
}

export function useAgents(): UseQueryResult<AgentDto[]> {
  return useQuery({ queryKey: queryKeys.agents, queryFn: agentsApi.list, staleTime: 120_000 });
}

/** RIM-187 - contagens do topo da Agenda. */
export function useCalendarSummary(from: string, to: string): UseQueryResult<CalendarSummaryDto> {
  return useQuery({
    queryKey: [...queryKeys.appointments, 'summary', { from, to }],
    queryFn: () => appointmentsApi.summary({ from, to }),
  });
}

/** ML138a - faltas por cliente no período visível. */
export function useNoShowsByClient(from: string, to: string): UseQueryResult<NoShowsByClientDto> {
  return useQuery({
    queryKey: [...queryKeys.appointments, 'no-shows-by-client', { from, to }],
    queryFn: () => appointmentsApi.noShowsByClient({ from, to }),
  });
}

export function useAppointments(range: {
  from: string;
  to: string;
}): UseQueryResult<Paginated<AppointmentDto>> {
  return useQuery({
    queryKey: [...queryKeys.appointments, range],
    queryFn: () => appointmentsApi.list({ ...range, limit: 300 }),
  });
}

export function useWaitlist(): UseQueryResult<WaitlistEntryDto[]> {
  return useQuery({ queryKey: queryKeys.waitlist, queryFn: appointmentsApi.waitlist });
}

function useWaitlistMutation<TInput>(mutationFn: (input: TInput) => Promise<WaitlistEntryDto>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.waitlist });
      void client.invalidateQueries({ queryKey: queryKeys.appointments });
    },
  });
}

export function useAddToWaitlist() {
  return useWaitlistMutation((body: CreateWaitlistEntryRequest) =>
    appointmentsApi.addToWaitlist(body),
  );
}

export function useOfferWaitlistSlot() {
  return useWaitlistMutation(({ id, startAt }: { id: string; startAt: string }) =>
    appointmentsApi.offerWaitlistSlot(id, startAt),
  );
}

export function useAcceptWaitlistOffer() {
  return useWaitlistMutation((id: string) => appointmentsApi.acceptWaitlistOffer(id));
}

export function useRemoveFromWaitlist() {
  return useWaitlistMutation(({ id, reason }: { id: string; reason: string }) =>
    appointmentsApi.removeFromWaitlist(id, reason),
  );
}

/** RIM-3.6 - a empresa configura o assistente pela tela, não pelo seed. */
export function useUpdateAgent(): ReturnType<
  typeof useMutation<AgentDto, Error, { id: string; body: UpdateAgentRequest }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateAgentRequest }) =>
      agentsApi.update(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents });
      void queryClient.invalidateQueries({ queryKey: queryKeys.onboarding });
    },
  });
}

export function useUpdateAgentTools(): ReturnType<
  typeof useMutation<AgentDto, Error, { id: string; enabledTools: ToolName[] }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabledTools }: { id: string; enabledTools: ToolName[] }) =>
      agentsApi.setTools(id, enabledTools),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.agents }),
  });
}

/**
 * RIM-150 - testar o assistente. Sem cache: cada teste é uma pergunta nova, e
 * reaproveitar a resposta anterior esconderia o efeito da mudança que acabou de
 * ser feita — que é exatamente o que se está testando.
 */
export function useRunPlayground(): ReturnType<
  typeof useMutation<
    PlaygroundResultDto,
    Error,
    { id: string; turns: PlaygroundTurnDto[]; memory?: PlaygroundMemory }
  >
> {
  return useMutation({
    mutationFn: ({
      id,
      turns,
      memory,
    }: {
      id: string;
      turns: PlaygroundTurnDto[];
      memory?: PlaygroundMemory;
    }) => agentsApi.playground(id, { turns, ...(memory ? { memory } : {}) }),
  });
}

/** Horário de funcionamento e bloqueios (seção 11.1). */
/**
 * R074 - o catálogo da clínica, editável por quem é dono dele.
 *
 * `todos` traz os desativados junto. A tela de configuração precisa deles:
 * esconder o que foi desativado faz a pessoa procurar um serviço que ainda
 * existe e criar um segundo com o mesmo nome.
 */
export function useClinicServices(todos = false): UseQueryResult<ClinicServiceDto[]> {
  return useQuery({
    queryKey: [...queryKeys.clinicServices, { todos }],
    queryFn: () => clinicServicesApi.list(todos),
  });
}

export function useProfessionals(enabled = true): UseQueryResult<ProfessionalDto[]> {
  return useQuery({
    queryKey: queryKeys.professionals,
    queryFn: professionalsApi.list,
    enabled,
  });
}

export function useClinicalResources(todos = false): UseQueryResult<ClinicalResourceDto[]> {
  return useQuery({
    queryKey: [...queryKeys.clinicalResources, { todos }],
    queryFn: () => clinicalResourcesApi.list(todos),
  });
}

/**
 * Toda escrita no catálogo invalida a AGENDA junto.
 *
 * Mudar a duração de um serviço muda os horários que a agenda pode oferecer, e
 * uma tela que continuasse mostrando a grade antiga faria a pessoa marcar em
 * cima do que acabou de mudar.
 */
function useCatalogoMutation<TEntrada, TSaida>(
  fn: (entrada: TEntrada) => Promise<TSaida>,
  chave: readonly unknown[],
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: chave });
      void client.invalidateQueries({ queryKey: queryKeys.appointments });
    },
  });
}

export function useCreateClinicService() {
  return useCatalogoMutation(
    (body: {
      name: string;
      code?: string | null;
      description?: string | null;
      durationMinutes: number;
      bufferMinutes?: number;
      priceCents?: number | null;
      specialty?: string | null;
      modalities?: Array<'presencial' | 'online' | 'domiciliar'>;
      requiredResourceTypes?: string[];
    }) => clinicServicesApi.create(body),
    queryKeys.clinicServices,
  );
}

export function useUpdateClinicService() {
  return useCatalogoMutation(
    (entrada: {
      id: string;
      body: Partial<Omit<ClinicServiceDto, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>>;
    }) => clinicServicesApi.update(entrada.id, entrada.body),
    queryKeys.clinicServices,
  );
}

export function useCreateProfessional() {
  return useCatalogoMutation(
    (body: {
      name: string;
      displayName?: string;
      specialty?: string | null;
      registrationType?: string | null;
      registrationNumber?: string | null;
      modalities?: Array<'presencial' | 'online' | 'domiciliar'>;
    }) => professionalsApi.create(body),
    queryKeys.professionals,
  );
}

export function useUpdateProfessional() {
  return useCatalogoMutation(
    (entrada: {
      id: string;
      body: Partial<Omit<ProfessionalDto, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>>;
    }) => professionalsApi.update(entrada.id, entrada.body),
    queryKeys.professionals,
  );
}

export function useCreateClinicalResource() {
  return useCatalogoMutation(
    (body: {
      code: string;
      name: string;
      type: 'room' | 'equipment';
      location?: string | null;
      cleanupBufferMinutes?: number;
    }) => clinicalResourcesApi.create(body),
    queryKeys.clinicalResources,
  );
}

export function useUpdateClinicalResource() {
  return useCatalogoMutation(
    (input: {
      id: string;
      body: Partial<Omit<ClinicalResourceDto, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>>;
    }) => clinicalResourcesApi.update(input.id, input.body),
    queryKeys.clinicalResources,
  );
}

export function useSetClinicalResourceStatus() {
  return useCatalogoMutation(
    (input: {
      id: string;
      isActive: boolean;
      reason: string;
      ownerName?: string;
      reviewDueAt?: string;
    }) =>
      clinicalResourcesApi.setStatus(input.id, {
        isActive: input.isActive,
        reason: input.reason,
        ownerName: input.ownerName,
        reviewDueAt: input.reviewDueAt,
        confirmed: true,
      }),
    queryKeys.clinicalResources,
  );
}

export function useAvailabilityRules(): UseQueryResult<AvailabilityRuleDto[]> {
  return useQuery({ queryKey: queryKeys.availabilityRules, queryFn: availabilityRulesApi.list });
}

/**
 * Mudar a agenda muda o que o assistente pode oferecer na próxima mensagem.
 * Por isso a invalidação alcança também os horários livres e a Agenda.
 */
function useAvailabilityMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<unknown>,
): ReturnType<typeof useMutation<unknown, Error, TVariables>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.availabilityRules });
      void queryClient.invalidateQueries({ queryKey: queryKeys.availability });
      void queryClient.invalidateQueries({ queryKey: queryKeys.appointments });
    },
  });
}

export function useSetWeeklyRule(): ReturnType<
  typeof useMutation<
    unknown,
    Error,
    {
      weekday: number;
      startTime: string;
      endTime: string;
      slotMinutes: number;
      capacity?: number;
      isActive?: boolean;
    }
  >
> {
  return useAvailabilityMutation(availabilityRulesApi.setWeekly);
}

export function useCreateBlock(): ReturnType<
  typeof useMutation<
    unknown,
    Error,
    {
      blockStartAt: string;
      blockEndAt: string;
      reason?: string;
      professionalId?: string;
      resourceId?: string;
    }
  >
> {
  return useAvailabilityMutation(availabilityRulesApi.createBlock);
}

export function useRemoveRule(): ReturnType<typeof useMutation<unknown, Error, string>> {
  return useAvailabilityMutation(availabilityRulesApi.remove);
}

export function useCompleteBlockTask(): ReturnType<
  typeof useMutation<unknown, Error, { ruleId: string; appointmentId: string; resolution: string }>
> {
  return useAvailabilityMutation((input) =>
    availabilityRulesApi.completeBlockTask(input.ruleId, input.appointmentId, input.resolution),
  );
}

/** Equipe da clínica: quem entra, com que papel, quem perde o acesso. */
export function useUsers(enabled = true): UseQueryResult<UserDto[]> {
  return useQuery({ queryKey: queryKeys.users, queryFn: usersApi.list, enabled });
}

export function useCreateUser(): ReturnType<
  typeof useMutation<CreateUserResultDto, Error, CreateUserRequest>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserRequest) => usersApi.create(body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.users }),
  });
}

export function useUpdateUser(): ReturnType<
  typeof useMutation<UserDto, Error, { id: string; body: UpdateUserRequest }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateUserRequest }) =>
      usersApi.update(id, body),
    onSuccess: (user) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users });
      // Ao conceder ou retirar o próprio acesso clínico ou financeiro, a navegação precisa
      // refletir a decisão imediatamente, sem exigir sair e entrar novamente.
      if (useAuthStore.getState().user?.id === user.id) useAuthStore.setState({ user });
    },
  });
}

/**
 * Dados da clínica. O `refreshSession` no fim não é detalhe: o nome e o fuso
 * ficam na sessão e aparecem no rodapé e nas datas de toda a interface.
 */
export function useUpdateCompany(): ReturnType<
  typeof useMutation<CompanyDto, Error, UpdateCompanyRequest>
> {
  const refresh = useAuthStore((state) => state.restore);
  return useMutation({
    mutationFn: (body: UpdateCompanyRequest) => companyApi.update(body),
    // Finish refreshing the session before closing the form or allowing another edit.
    onSuccess: () => refresh(),
  });
}

export function useChangePassword(): ReturnType<
  typeof useMutation<{ changed: boolean }, Error, ChangePasswordRequest>
> {
  return useMutation({ mutationFn: usersApi.changePassword });
}

/**
 * O que falhou (seção 12.4).
 *
 * `staleTime` zero: quem abre esta tela está investigando alguma coisa AGORA, e
 * um dado de cinco minutos atrás poderia esconder a falha que acabou de
 * acontecer.
 */
export function useDiagnostics(): UseQueryResult<DiagnosticsDto> {
  return useQuery({
    queryKey: queryKeys.diagnostics,
    queryFn: diagnosticsApi.get,
    staleTime: 0,
  });
}

/**
 * R010 - a saude por capacidade, com o instante da leitura.
 *
 * `staleTime: 0` pelo mesmo motivo do diagnostico: um painel de saude de cinco
 * minutos atras esconde exatamente a falha que acabou de acontecer.
 */
export function useCapabilityHealth(): UseQueryResult<CapabilityHealthReportDto> {
  return useQuery({
    queryKey: queryKeys.capabilityHealth,
    queryFn: diagnosticsApi.health,
    staleTime: 0,
  });
}

export function useIntegrationHomologations(): UseQueryResult<IntegrationHomologationReportDto> {
  return useQuery({
    queryKey: queryKeys.integrationHomologations,
    queryFn: diagnosticsApi.integrationHomologations,
    staleTime: 0,
  });
}

export function useExternalReadiness(): UseQueryResult<ExternalReadinessReportDto> {
  return useQuery({
    queryKey: queryKeys.externalReadiness,
    queryFn: diagnosticsApi.externalReadiness,
    staleTime: 0,
  });
}

export function useUpdateIntegrationHomologation(): ReturnType<
  typeof useMutation<
    IntegrationHomologationDto,
    Error,
    {
      kind: IntegrationHomologationKind;
      body: UpdateIntegrationHomologationRequest;
    }
  >
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, body }) => diagnosticsApi.updateIntegrationHomologation(kind, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.integrationHomologations });
      void queryClient.invalidateQueries({ queryKey: queryKeys.externalReadiness });
    },
  });
}

/** Canais da empresa, para a tela de Configurações dizer o que está conectado. */
export function useChannels(enabled = true): UseQueryResult<ChannelDto[]> {
  return useQuery({ queryKey: queryKeys.channels, queryFn: channelsApi.list, enabled });
}

export function useKnowledge(
  enabled = true,
  filter: { page?: number; search?: string; audience?: string } = {},
): UseQueryResult<Paginated<KnowledgeItemDto>> {
  const identity = useAuthStore((s) => [s.company?.id, s.user?.id, s.user?.role].join(':'));
  return useQuery({
    queryKey: [
      ...queryKeys.knowledge,
      identity,
      filter.page ?? 1,
      filter.search ?? '',
      filter.audience ?? '',
    ],
    enabled,
    queryFn: () => knowledgeApi.list({ limit: 200, ...filter }),
    staleTime: 300_000,
  });
}

export function useFollowUps(status = 'pending'): UseQueryResult<Paginated<FollowUpDto>> {
  return useQuery({
    queryKey: [...queryKeys.followups, { status }],
    queryFn: () => followUpsApi.list({ status, limit: 50 }),
  });
}

export function useCancelFollowUp(): ReturnType<typeof useMutation<FollowUpDto, Error, string>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => followUpsApi.cancel(id),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.followups });
      void queryClient.invalidateQueries({ queryKey: queryKeys.activities });
    },
  });
}

export function useSendFollowUpNow(): ReturnType<typeof useMutation<FollowUpDto, Error, string>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => followUpsApi.sendNow(id),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.followups });
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversationList });
      void queryClient.invalidateQueries({ queryKey: queryKeys.activities });
    },
  });
}

/**
 * EPIC 17 - o Inicio nao soma nada por conta propria.
 * `refetchInterval` nao existe de proposito: a secao 7.3 diz que o frontend
 * invalida estas queries quando chega evento de dominio, sem contador dedicado.
 */
export function useDashboard(
  period: AnalyticsPeriod,
  enabled = true,
): UseQueryResult<DashboardDto> {
  return useQuery({
    queryKey: queryKeys.dashboard(period),
    queryFn: () => analyticsApi.dashboard(period),
    enabled,
  });
}

/** EPIC 18 - Resultados. */
export function useInsights(period: AnalyticsPeriod): UseQueryResult<InsightsDto> {
  return useQuery({
    queryKey: queryKeys.insights(period),
    queryFn: () => analyticsApi.insights(period),
  });
}

export function useMediaAnalytics(
  period: AnalyticsPeriod,
  kind: MediaAnalyticsKind = 'all',
  state: MediaAnalyticsState = 'all',
): UseQueryResult<MediaAnalyticsDto> {
  return useQuery({
    queryKey: queryKeys.mediaAnalytics(period, kind, state),
    queryFn: () => analyticsApi.media(period, kind, state),
  });
}

export function useMediaGovernance() {
  return useQuery({ queryKey: ['media-governance'], queryFn: mediaGovernanceApi.get });
}

export function useRequestMediaDeletion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      mediaGovernanceApi.requestDeletion(id, reason),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['media-governance'] }),
  });
}

export function useReviewMediaDeletion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      reason,
    }: {
      id: string;
      decision: 'approve' | 'deny';
      reason: string;
    }) => mediaGovernanceApi.reviewDeletion(id, decision, reason),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['media-governance'] }),
  });
}

export function useDataGovernancePolicy() {
  return useQuery({ queryKey: ['data-governance'], queryFn: dataGovernanceApi.get });
}

export function useSecurityReadiness() {
  return useQuery({
    queryKey: ['security-readiness'],
    queryFn: diagnosticsApi.securityReadiness,
  });
}

export function useUpdateDataGovernancePolicy() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: dataGovernanceApi.update,
    onSuccess: (data) => {
      client.setQueryData(['data-governance'], data);
      void client.invalidateQueries({ queryKey: queryKeys.externalReadiness });
    },
  });
}

export function useApproveDataGovernancePolicy() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: dataGovernanceApi.approve,
    onSuccess: (data) => {
      client.setQueryData(['data-governance'], data);
      void client.invalidateQueries({ queryKey: queryKeys.externalReadiness });
    },
  });
}

/** RIM-189 - resultados por automacao. */
export function useAutomationResults(): UseQueryResult<AutomationResultDto[]> {
  return useQuery({
    queryKey: [...queryKeys.automations, 'results'],
    queryFn: () => automationsApi.results(),
  });
}

export function useAutomations(): UseQueryResult<AutomationDto[]> {
  return useQuery({ queryKey: queryKeys.automations, queryFn: automationsApi.list });
}

export function useUpdateAutomation(): ReturnType<
  typeof useMutation<
    AutomationDto,
    Error,
    {
      template: AutomationTemplate;
      body: { isActive?: boolean; steps?: { delayHours: number; message: string }[] };
    }
  >
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      template,
      body,
    }: {
      template: AutomationTemplate;
      body: { isActive?: boolean; steps?: { delayHours: number; message: string }[] };
    }) => automationsApi.update(template, body),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.automations });
    },
  });
}

/** RIM-157 - edicao manual da oportunidade. */
export function useUpdateOpportunity(): ReturnType<
  typeof useMutation<
    OpportunityDto,
    Error,
    {
      id: string;
      title?: string;
      estimatedValue?: number | null;
      source?: OpportunityDto['source'];
      externalReferrerName?: string | null;
    }
  >
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      title?: string;
      estimatedValue?: number | null;
      source?: OpportunityDto['source'];
      externalReferrerName?: string | null;
    }) => opportunitiesApi.update(id, body),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.opportunities });
      void queryClient.invalidateQueries({ queryKey: queryKeys.activities });
    },
  });
}

export function useMoveStage(): ReturnType<
  typeof useMutation<OpportunityDto, Error, { id: string; stageKey: string }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stageKey }: { id: string; stageKey: string }) =>
      opportunitiesApi.moveStage(id, stageKey),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.opportunities });
      void queryClient.invalidateQueries({ queryKey: queryKeys.activities });
    },
  });
}

export function useUpdateContact(): ReturnType<
  typeof useMutation<ContactDto, Error, { id: string; body: UpdateContactRequest }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateContactRequest }) =>
      contactsApi.update(id, body),
    onSuccess: (contact) => {
      queryClient.setQueryData(queryKeys.contact(contact.id), contact);
      void queryClient.invalidateQueries({ queryKey: queryKeys.contacts });
      void queryClient.invalidateQueries({ queryKey: queryKeys.activitiesByContact(contact.id) });
    },
  });
}

/** RIM-083/084/085 - troca de quem atende a conversa. */
export function useConversationMode(): ReturnType<
  typeof useMutation<
    ConversationDto,
    Error,
    { id: string; action: 'takeover' | 'release' | 'pause' }
  >
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'takeover' | 'release' | 'pause' }) =>
      conversationsApi[action](id),

    /**
     * So invalida — nao escreve a resposta no cache.
     *
     * Escrever parecia mais seguro (o servidor acabou de gravar; por que
     * perguntar de novo?), mas no React Query v5 TODO callback do hook roda
     * antes dos callbacks de quem chamou `mutate`. A escrita re-renderiza a
     * tela na hora e o `onSuccess` do componente nao chega a rodar — o aviso
     * "Voce assumiu esta conversa" desaparecia.
     *
     * Invalidar e confiavel aqui porque a chave da lista deixou de derrubar
     * toda thread aberta (veja `queryKeys.ts`): sem aquela enxurrada de
     * buscas, a recarga da lista nao esbarra mais no limite de requisicoes.
     */
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversationList });
      void queryClient.invalidateQueries({ queryKey: queryKeys.activities });
    },
  });
}

export function useSendMessage(): ReturnType<
  typeof useMutation<MessageDto, Error, { id: string; text: string }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => conversationsApi.send(id, text),
    onSuccess: (_message, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages(variables.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversationList });
    },
  });
}

export function useMarkConversationRead(): ReturnType<
  typeof useMutation<ConversationDto, Error, string>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => conversationsApi.markRead(conversationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversationList });
    },
  });
}

/**
 * CONVERSA CENTRAL - os hooks da gestao por conversa.
 *
 * A conversa e a lista de recentes tem chaves separadas de proposito: enviar
 * uma mensagem muda as duas (a conversa ganha mensagens, a lista muda de ordem
 * e de titulo), mas abrir uma conversa nao precisa rebuscar a lista.
 */
export function ownWorkChatIdentity(state: ReturnType<typeof useAuthStore.getState>): string {
  return [
    state.status,
    state.features.rimatiChat,
    state.company?.id,
    state.user?.id,
    state.user?.role,
    state.user?.isActive ?? true,
    state.user?.clinicalAccess ?? 'none',
    state.company?.vertical ?? 'generic',
  ].join(':');
}

function useRimatiChannel(purpose?: RimatiChatPurpose | null) {
  const identity = useAuthStore(ownWorkChatIdentity);
  const ownWork = purpose === 'own-work';
  const reader = purpose === 'knowledge-reader';
  const restricted = ownWork || reader;
  const prefix = ownWork ? 'own-work-chat' : 'knowledge-reader-chat';
  const current = () => {
    const state = useAuthStore.getState();
    return (
      !restricted ||
      (ownWorkChatIdentity(state) === identity &&
        state.status === 'authenticated' &&
        state.features.rimatiChat &&
        !!state.company?.id &&
        !!state.user?.id &&
        state.user.isActive !== false &&
        USER_ROLES.includes(state.user.role))
    );
  };
  async function authorized<T>(read: () => Promise<T>): Promise<T> {
    if (!current())
      throw new ApiError(
        403,
        'OWN_WORK_CONTEXT_CHANGED',
        'Seu acesso mudou. Abra a conversa novamente.',
      );
    const result = await read();
    if (!current())
      throw new ApiError(
        403,
        'OWN_WORK_CONTEXT_CHANGED',
        'Seu acesso mudou. Abra a conversa novamente.',
      );
    return result;
  }
  return {
    api: rimatiChatChannel(purpose),
    identity,
    ownWork,
    restricted,
    current,
    authorized,
    list: restricted ? [prefix, identity, 'list'] : queryKeys.rimatiChats,
    session: (id: string) =>
      restricted ? [prefix, identity, 'session', id] : queryKeys.rimatiChat(id),
    options: restricted
      ? {
          gcTime: 0,
          staleTime: 0,
          refetchOnMount: 'always' as const,
          retry: false as const,
          refetchInterval: ownWork ? 15000 : (false as const),
        }
      : {},
  };
}

export function useRimatiChats(
  enabled = true,
  purpose?: RimatiChatPurpose | null,
): UseQueryResult<AdminChatSessionDto[]> {
  const channel = useRimatiChannel(purpose);
  return useQuery({
    queryKey: channel.list,
    queryFn: () => channel.authorized(() => channel.api.list()),
    enabled: enabled && channel.current(),
    ...channel.options,
  });
}

export function useRimatiChat(
  id: string | null,
  purpose?: RimatiChatPurpose | null,
): UseQueryResult<AdminChatViewDto> {
  const channel = useRimatiChannel(purpose);
  return useQuery({
    queryKey: channel.session(id ?? 'nenhuma'),
    queryFn: () => channel.authorized(() => channel.api.session(id as string)),
    enabled: Boolean(id) && channel.current(),
    refetchOnMount: 'always',
    retry: false,
    ...channel.options,
  });
}

export function useCreateRimatiChat(purpose?: RimatiChatPurpose | null) {
  const channel = useRimatiChannel(purpose);
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof rimatiChatApi.create>[0]) =>
      channel.authorized(() => channel.api.create(body)),
    onSuccess: () => {
      if (channel.current()) void client.invalidateQueries({ queryKey: channel.list });
    },
  });
}

export function useSendRimatiMessage(id: string | null, purpose?: RimatiChatPurpose | null) {
  const channel = useRimatiChannel(purpose);
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      text: string;
      period?: AdminPeriod;
      channel?: string | null;
      clientRequestId?: string;
      screenContext?: ScreenContextDto;
      signal?: AbortSignal;
    }) => {
      const { signal, ...payload } = body;
      return channel.authorized(() => channel.api.send(id as string, payload, signal));
    },
    onSuccess: (resposta) => {
      if (!channel.current()) return;
      const view = chatViewFromReply(resposta);
      /*
        A resposta ja traz as duas mensagens novas: escrever no cache evita o
        piscar entre "enviei" e "chegou". A invalidacao vem depois, para a
        conversa acabar igual ao servidor mesmo se algo mudou em paralelo.
      */
      client.setQueryData<AdminChatViewDto>(channel.session(id as string), (atual) =>
        atual
          ? {
              session: resposta.session,
              messages: [
                ...new Map([...atual.messages, ...view.messages].map((m) => [m.id, m])).values(),
              ],
              proposals: [
                ...new Map([...atual.proposals, ...view.proposals].map((m) => [m.id, m])).values(),
              ],
              tasks: [...new Map([...atual.tasks, ...view.tasks].map((m) => [m.id, m])).values()],
              journey: view.journey,
            }
          : view,
      );
      void client.invalidateQueries({ queryKey: channel.list });
      if (channel.restricted)
        void client.invalidateQueries({ queryKey: channel.session(id as string) });
    },
  });
}

export function useArchiveRimatiChat(purpose?: RimatiChatPurpose | null) {
  const channel = useRimatiChannel(purpose);
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => channel.authorized(() => channel.api.archive(id)),
    onSuccess: () => {
      if (channel.current()) void client.invalidateQueries({ queryKey: channel.list });
    },
  });
}

/**
 * INICIO - abrir uma conversa JA com a primeira pergunta.
 *
 * Uma mutacao so, e nao "criar" seguida de "enviar" no componente: entre as
 * duas chamadas o React ainda nao re-renderizou, entao um hook parametrizado
 * pelo id da sessao continuaria com o id anterior e a mensagem iria para lugar
 * nenhum. Aqui o id nasce e e usado dentro da mesma funcao.
 */
export function useAskRimati(options?: { onSessionReady?: (id: string) => void }) {
  const client = useQueryClient();
  const controller = useRef<AbortController | null>(null);
  const handedOff = useRef(false);
  useEffect(
    () => () => {
      if (!handedOff.current) controller.current?.abort();
    },
    [],
  );
  const mutation = useMutation({
    mutationFn: async (text: string) => {
      handedOff.current = false;
      controller.current = new AbortController();
      const sessao = await rimatiChatApi.create({});
      const clientRequestId = crypto.randomUUID();
      sessionStorage.setItem(`rimati-pending:${sessao.id}`, clientRequestId);
      sessionStorage.setItem(
        `rimati-pending-body:${sessao.id}`,
        JSON.stringify({
          requestId: clientRequestId,
          text,
          createdAt: new Date().toISOString(),
          previousMessageIds: [],
        }),
      );
      try {
        const sending = rimatiChatApi.send(
          sessao.id,
          { text, clientRequestId },
          AbortSignal.any([controller.current.signal, AbortSignal.timeout(60000)]),
        );
        if (options?.onSessionReady) {
          // Ownership moves to the durable conversation, which restores this same
          // pending body/ID. Unmount must not abort the already-started request.
          handedOff.current = true;
          try {
            options.onSessionReady(sessao.id);
          } catch {
            handedOff.current = false;
          }
        }
        const resposta = await sending;
        sessionStorage.removeItem(`rimati-pending:${sessao.id}`);
        sessionStorage.removeItem(`rimati-pending-body:${sessao.id}`);
        return resposta;
      } catch {
        // Open the durable conversation so a lost response is checked, not resent.
        return { session: sessao, messages: [], proposals: [], tasks: [], journey: null };
      }
    },
    onSuccess: (resposta) => {
      const key = queryKeys.rimatiChat(resposta.session.id);
      // The destination may already have recovered newer messages or confirmed a
      // proposal. A late send (especially its empty recovery fallback) cannot
      // replace that authoritative view with an older snapshot.
      if (client.getQueryData<AdminChatViewDto>(key))
        void client.invalidateQueries({ queryKey: key });
      else client.setQueryData<AdminChatViewDto>(key, chatViewFromReply(resposta));
      void client.invalidateQueries({ queryKey: queryKeys.rimatiChats });
    },
  });
  return { ...mutation, cancelWait: () => controller.current?.abort() };
}

/**
 * A decisao de uma proposta da conversa central.
 *
 * Separada de `useDecideProposal` porque o que precisa ser invalidado e outro:
 * la e a sessao de configuracao; aqui e a conversa (a proposta some da lista de
 * pendentes) MAIS tudo que a aprovacao mudou no dominio — agenda, conhecimento,
 * automacoes. Sem isso, a tela ao lado continuaria mostrando o horario antigo
 * ate alguem recarregar.
 */
export function useDecideAdminProposal(sessionId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DecideProposalRequest }) =>
      rimatiChatApi.decide(id, body),
    onSuccess: () => {
      void useAuthStore.getState().restore();
      void client.invalidateQueries({ queryKey: queryKeys.onboarding });
      if (sessionId) {
        void client.invalidateQueries({ queryKey: queryKeys.rimatiChat(sessionId) });
      }
      for (const chave of [
        queryKeys.availabilityRules,
        queryKeys.availability,
        queryKeys.knowledge,
        queryKeys.automations,
        queryKeys.agents,
        queryKeys.appointments,
      ]) {
        void client.invalidateQueries({ queryKey: chave });
      }
    },
  });
}

/**
 * As acoes sobre um plano de acompanhamento.
 *
 * Todas invalidam a conversa E os acompanhamentos: aprovar cria envios que a
 * pagina de Automacoes e o Inicio mostram, e deixar so a conversa atualizada
 * faria a tela ao lado continuar dizendo que nada foi programado.
 */
export function useRimatiTask(sessionId: string | null) {
  const client = useQueryClient();

  const invalidar = (): void => {
    if (sessionId) void client.invalidateQueries({ queryKey: queryKeys.rimatiChat(sessionId) });
    void client.invalidateQueries({ queryKey: queryKeys.followups });
    void client.invalidateQueries({ queryKey: queryKeys.activities });
  };

  const approve = useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      rimatiChatApi.approveTask(id, version),
    onSuccess: invalidar,
  });
  const edit = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { remove?: string[]; message?: string } }) =>
      rimatiChatApi.editTask(id, body),
    onSuccess: invalidar,
  });
  const resume = useMutation({
    mutationFn: (id: string) => rimatiChatApi.resumeTask(id),
    onSuccess: invalidar,
  });
  const cancel = useMutation({
    mutationFn: (id: string) => rimatiChatApi.cancelTask(id),
    onSuccess: invalidar,
  });

  return { approve, edit, resume, cancel };
}

/** INICIO - as prioridades do dia. */
export function usePriorities(): UseQueryResult<PriorityFeedDto> {
  return useQuery({ queryKey: queryKeys.priorities, queryFn: () => priorityApi.feed() });
}

export function useDismissPriority() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, fingerprint }: { key: string; fingerprint: string }) =>
      priorityApi.dismiss(key, fingerprint),
    /*
      A resposta ja traz a lista nova: escrever no cache evita o piscar entre
      "dispensei" e "sumiu".
    */
    onSuccess: (feed) => client.setQueryData(queryKeys.priorities, feed),
  });
}

/** SECAO 15.4 - renomear uma conversa. */
export function useRenameRimatiChat(purpose?: RimatiChatPurpose | null) {
  const channel = useRimatiChannel(purpose);
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      channel.authorized(() => channel.api.rename(id, title)),
    onSuccess: (sessao) => {
      if (!channel.current()) return;
      /*
        A conversa aberta E a lista de recentes: o titulo aparece nas duas, e
        atualizar so uma deixaria a lista mostrando o nome velho.
      */
      client.setQueryData<AdminChatViewDto>(channel.session(sessao.id), (atual) =>
        atual ? { ...atual, session: sessao } : atual,
      );
      void client.invalidateQueries({ queryKey: channel.list });
    },
  });
}

/**
 * R109 - o que falta para a clínica começar.
 *
 * `enabled` é parâmetro, e não decisão interna: a rota só responde a quem
 * configura, e disparar a consulta para quem atende produziria um 403 previsível
 * a cada carregamento do Início — ruído no diagnóstico e uma chamada que já se
 * sabia que ia falhar.
 *
 * `staleTime` zero porque o estado muda ao lado: a pessoa cadastra o horário
 * numa aba e volta para o Início esperando o passo riscado.
 */
export function useOnboarding(enabled: boolean): UseQueryResult<OnboardingDto> {
  return useQuery({
    queryKey: queryKeys.onboarding,
    queryFn: onboardingApi.get,
    staleTime: 0,
    enabled,
  });
}

/** ML136 - funil de ativação; só consulta quando quem pede é operador da plataforma. */
export function useAtivacaoDoOperador(
  enabled: boolean,
  dias = 30,
): UseQueryResult<AtivacaoFunilDto> {
  return useQuery({
    queryKey: [...queryKeys.plataformaAtivacao, dias],
    queryFn: () => plataformaApi.ativacao(dias),
    staleTime: 30_000,
    enabled,
  });
}

/**
 * R058 - a visão do operador.
 *
 * `enabled` é parâmetro porque a rota responde 404 a quem não é operador — e
 * disparar a consulta para todo mundo encheria o diagnóstico de erros que já se
 * sabia que iam acontecer.
 */
export function useVisaoDoOperador(
  enabled: boolean,
  dias = 30,
): UseQueryResult<VisaoDoOperadorDto> {
  return useQuery({
    queryKey: [...queryKeys.plataformaVisao, dias],
    queryFn: () => plataformaApi.visao(dias),
    staleTime: 30_000,
    enabled,
  });
}

/**
 * R059 - os relatos desta clínica.
 *
 * `staleTime` curto porque o andamento muda do outro lado: quem relatou um
 * problema volta à tela justamente para ver se mudou de estado.
 */
export function useMeusRelatos(enabled = true): UseQueryResult<TicketDto[]> {
  return useQuery({
    queryKey: queryKeys.meusRelatos,
    queryFn: suporteApi.meus,
    staleTime: 15_000,
    enabled,
  });
}

/** R060 - a fila do operador. Recusada com 404 para quem não opera. */
export function useFilaDeSuporte(
  enabled: boolean,
  state?: string,
): UseQueryResult<TicketDaFilaDto[]> {
  return useQuery({
    queryKey: [...queryKeys.plataformaFila, state ?? 'todos'],
    queryFn: () => plataformaApi.fila(state),
    staleTime: 15_000,
    enabled,
  });
}

/** R061 - problemas que atingem mais de uma empresa. */
export function useIncidentes(enabled: boolean, dias = 7): UseQueryResult<IncidenteDto[]> {
  return useQuery({
    queryKey: [...queryKeys.plataformaIncidentes, dias],
    queryFn: () => plataformaApi.incidentes(dias),
    staleTime: 30_000,
    enabled,
  });
}

/**
 * R063 - sugestões com evidência.
 *
 * `staleTime` longo de propósito: cada consulta gasta crédito de modelo, e um
 * painel que regenera a cada troca de aba transformaria leitura em fatura.
 */
export function useSugestoes(enabled: boolean, dias = 14): UseQueryResult<SugestoesDto> {
  return useQuery({
    queryKey: [...queryKeys.plataformaSugestoes, dias],
    queryFn: () => plataformaApi.sugestoes(dias),
    staleTime: 10 * 60_000,
    enabled,
  });
}

export function useStoreMediaStatus(enabled: boolean) {
  return useQuery({ queryKey: ['store-media-status'], queryFn: storeMediaApi.status, enabled });
}
export function useStoreMediaList(enabled: boolean) {
  return useQuery({
    queryKey: ['store-media-list'],
    queryFn: () => storeMediaApi.list(),
    enabled,
  });
}
export function useStoreMediaAsset(id: string | null | undefined) {
  return useQuery({
    queryKey: ['store-media-asset', id],
    queryFn: () => storeMediaApi.get(id!),
    enabled: !!id,
  });
}

/** Prévia real da foto da biblioteca (bytes pela rota da gestão); devolve uma URL local do blob. */
export function useStoreMediaPreview(id: string | null | undefined) {
  return useQuery({
    queryKey: ['store-media-preview', id],
    queryFn: async () => URL.createObjectURL(await storeMediaApi.preview(id!)),
    enabled: !!id,
    staleTime: Infinity,
    gcTime: 0,
  });
}
